import { ChangeDetectionStrategy, Component, inject, input, output } from "@angular/core";
import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatDivider } from "@angular/material/divider";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { MatOption, MatSelect } from "@angular/material/select";
import { FormsModule } from "@angular/forms";
import { AgentDefinition } from "../../model/agent.types";
import { DebugModeService } from "../../services/debug-mode.service";
import { DebugStreamService } from "../../services/debug-stream.service";
import { AiChatSessionService } from "../../services/ai-chat-session.service";
import { MatTooltip } from "@angular/material/tooltip";
import { ChatThreadListComponent } from "../chat-thread-list/chat-thread-list.component";

/**
 * Shared header panel for all chat modes (Sidebar, Bubble).
 * Renders the teal header bar with agent selector, settings menu,
 * optional refresh and close buttons.
 */
@Component({
  selector: "lib-chat-panel-hdr",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatOption,
    MatSelect,
    MatTooltip,
    MatDivider,
    ChatThreadListComponent,
  ],
  templateUrl: "./chat-panel-hdr.component.html",
  styleUrl: "./chat-panel-hdr.component.scss",
})
export class ChatPanelHdrComponent {
  protected readonly debugMode = inject(DebugModeService);

  protected readonly debugStream = inject(DebugStreamService);

  private readonly chatSession = inject(AiChatSessionService);

  /** Display title shown when there is only one agent. */
  readonly title = input<string>("AI Assistant");

  /** List of available agents; shows a select when length > 1. */
  readonly agents = input<AgentDefinition[]>([]);

  /** Currently selected agent id (two-way via agentChange output). */
  readonly selectedAgentId = input<string>("");

  /** Whether to render the minimize button. */
  readonly showMinimize = input<boolean>(false);

  /** Whether to render the reset-position/size button. */
  readonly showReset = input<boolean>(false);

  /** Emitted when the user picks a different agent from the select. */
  readonly agentChange = output<string>();

  /** Emitted when the minimize button is clicked. */
  readonly minimizeClicked = output<void>();

  /** Emitted when "New chat" is selected from the settings menu. */
  readonly newChatRequested = output<void>();

  /** Emitted when the reset-position/size button is clicked. */
  readonly resetClicked = output<void>();

  /** Loads the thread list when the settings menu is opened. */
  protected onSettingsMenuOpened(): void {
    this.chatSession.loadThreads();
  }
}
